import React, { Fragment, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GlobalContext } from "../../../Context/GlobalContext";
import { Dialog, Transition } from "@headlessui/react";
import { Toaster, toast } from "react-hot-toast";
import { Building2, Plus, Trash2, QrCode, RefreshCcw, ChevronRight } from "lucide-react";
import AdminSpecialOffers from "./AdminSpecialOffers"; // adjust path

import useDining from "../../Services/OrderServices/DiningServices/useDining";
import ClientSearchSelect from "../../common/ClientSearchSelect";

/** small helper */
const cls = (...s) => s.filter(Boolean).join(" ");

export default function AdminDining() {

  const {getRestaurantTables, addRestaurantTable, deleteDiningTable }=useDining();

  const navigate = useNavigate();
  const { api, AllClients = [] } = useContext(GlobalContext);

  // client selection (from context list)
  const [userID, setUserID] = useState("");
  const [clientName, setClientName] = useState("");

  // tables
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);

  // add table modal
  const [addOpen, setAddOpen] = useState(false);
  const [newTableNo, setNewTableNo] = useState("");

  // delete confirm modal
  const [delOpen, setDelOpen] = useState(false);
  const [delBusy, setDelBusy] = useState(false);
  const [deleteTableNo, setDeleteTableNo] = useState("");




  const [offerModalOpen, setOfferModalOpen] = useState(false);

  // initial UX chip
  const hasClient = useMemo(() => !!userID, [userID]);

  const onClientChange = (e) => {
    const val = e.target.value;
    if (!val) {
      setUserID("");
      setClientName("");
      setTables([]);
      return;
    }
    const obj = JSON.parse(val);
    setUserID(obj.code);
    setClientName(obj.name);
  };

const fetchTables = async () => {
  if (!userID) return;

  setLoading(true);

  try {
    const result =
      await getRestaurantTables(userID);

    if (result.success) {
      setTables(result.data);
    } else {
      setTables([]);
      toast.error(result.message);
    }
  } finally {
    setLoading(false);
  }
};


const addTable = async () => {
  if (!newTableNo.trim()) return;

  try {
    const result = await addRestaurantTable({
      userid: userID,
      tableNo: newTableNo.trim(),
    });

    if (result.success) {
      toast.success(result.message);
      setNewTableNo("");
      setAddOpen(false);
      fetchTables();
    } else {
      toast.error(result.message);
    }
  } catch {
    toast.error("Error adding table");
  }
};


  const askDelete = (tableNo) => {
    setDeleteTableNo(String(tableNo));
    setDelOpen(true);
  };
const confirmDelete = async () => {
  if (!deleteTableNo) return;

  setDelBusy(true);

  try {
    const result = await deleteDiningTable({
      userid: userID,
      tableNo: deleteTableNo,
    });

    if (result.success) {
      toast.success(result.message);
      setDelOpen(false);
      setDeleteTableNo("");
      fetchTables();
    } else {
      toast.error(result.message);
    }
  } finally {
    setDelBusy(false);
  }
};


  useEffect(() => {
    if (userID) fetchTables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userID]);

  return (
    <div className="p-3 lg:p-5">
      <Toaster position="top-right" />

      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#01C37D]/10 text-[#01C37D]">
            <Building2 size={18} />
          </div>
          <div>
            <h1 className="text-xl font-semibold">Restaurant Dining</h1>
            <p className="text-sm text-gray-500">Manage tables, print QR codes, handle dining flow</p>
          </div>
        </div>
        <div className="flex gap-2">  <button
          className="inline-flex h-9 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50 disabled:opacity-60"
          onClick={fetchTables}
          disabled={!hasClient || loading}
        >
          <RefreshCcw size={16} />
          {loading ? "Refreshing…" : "Refresh"}
        </button>
          <button
            className="inline-flex h-9 items-center gap-2 rounded-xl bg-[#01C37D] px-3 text-sm font-medium text-white hover:bg-[#01C37D]/90"
            onClick={() => setOfferModalOpen(true)}
          >
            Manage Special Offers
          </button></div>

      </div>

      {/* Select Client */}
      <div className="rounded-2xl border bg-white p-3">
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <div className="mb-1 text-xs text-gray-500">Select Restaurant / Client</div>
           <ClientSearchSelect
  clients={AllClients || []}
  value={userID}
  className="w-full"
  placeholder={AllClients?.length ? "Select Client…" : "Loading clients…"}
  onChange={(client) => {
    setUserID(client?.us_code || "");
    setClientName(client?.us_name || "");
  }}
/>
          </div>

          <div className="flex items-end justify-end gap-2">
            <button
              className="inline-flex h-10 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50 disabled:opacity-60"
              onClick={() => setAddOpen(true)}
              disabled={!hasClient}
            >
              <Plus size={16} />
              Add New Table
            </button>

            <button
              className="inline-flex h-10 items-center gap-2 rounded-xl bg-[#01C37D] px-3 text-sm font-medium text-white hover:bg-[#01C37D]/90 disabled:opacity-60"
              onClick={() => navigate(`/admin/orders/Dinning/qrcodes/${userID}/${encodeURIComponent(clientName)}`)}
              disabled={!hasClient}
              title="Download QR Codes"
            >
              <QrCode size={16} />
              Download QR Codes
            </button>
          </div>
        </div>
      </div>

      {/* Tables Grid */}
      {hasClient && (
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {loading && tables.length === 0 && (
            <div className="col-span-full rounded-2xl border bg-white p-6 text-center text-sm text-gray-500">
              Loading tables…
            </div>
          )}

          {!loading && tables.length === 0 && (
            <div className="col-span-full rounded-2xl border bg-white p-6 text-center text-sm text-gray-500">
              No tables yet. Add your first table.
            </div>
          )}

          {tables.map((t) => {
            const booked = String(t.status) !== "Open";
            return (
              <div key={`${t.id || t.tableNo}`} className="rounded-2xl border bg-white p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs text-gray-500">Table</div>
                    <div className="text-xl font-semibold">#{t.tableNo}</div>
                  </div>
                  <button
                    className="inline-flex items-center gap-2 rounded-xl border px-3 py-1.5 text-xs hover:bg-gray-50"
                    onClick={() => askDelete(t.tableNo)}
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                </div>

                <div className="mt-3 flex items-center justify-between rounded-xl border px-3 py-2">
                  <div className="text-sm text-gray-500">Status</div>
                  <span
                    className={cls(
                      "rounded-full border px-2.5 py-0.5 text-xs font-semibold",
                      booked
                        ? "border-amber-300 bg-amber-50 text-amber-700"
                        : "border-emerald-300 bg-emerald-50 text-emerald-700"
                    )}
                  >
                    {booked ? "Booked" : "Available"}
                  </span>
                </div>

                <button
                  className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-gray-50"
                  onClick={() => navigate(`/admin/orders/Dinning/qrcodes/${userID}/${encodeURIComponent(clientName)}`)}
                >
                  <QrCode size={16} />
                  Table QR Options
                  <ChevronRight size={14} className="opacity-70" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Table Modal */}
      <Modal show={addOpen} onClose={() => setAddOpen(false)} title="Add New Table">
        <div className="space-y-3">
          <label className="block text-sm">
            <div className="mb-1 text-xs text-gray-500">Table Number</div>
            <input
              className="h-10 w-full rounded-xl border px-3 text-sm outline-none"
              placeholder="Enter table number"
              value={newTableNo}
              onChange={(e) => setNewTableNo(e.target.value)}
            />
          </label>
          <div className="flex justify-end gap-2">
            <button className="h-9 rounded-xl border px-3 text-sm" onClick={() => setAddOpen(false)}>
              Cancel
            </button>
            <button
              className="h-9 rounded-xl bg-[#01C37D] px-4 text-sm font-medium text-white disabled:opacity-60"
              onClick={addTable}
              disabled={!newTableNo.trim()}
            >
              Add Table
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirm */}
      <Modal show={delOpen} onClose={() => !delBusy && setDelOpen(false)} title="Delete Table" maxWidth="max-w-md">
        <div className="space-y-3">
          <div className="text-sm">
            Are you sure you want to delete <b>Table #{deleteTableNo}</b>?
          </div>
          <div className="flex justify-end gap-2">
            <button className="h-9 rounded-xl border px-3 text-sm" onClick={() => setDelOpen(false)} disabled={delBusy}>
              Cancel
            </button>
            <button
              className="h-9 rounded-xl bg-rose-600 px-4 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-60"
              onClick={confirmDelete}
              disabled={delBusy}
            >
              {delBusy ? "Deleting…" : "Yes, delete"}
            </button>
          </div>
        </div>
      </Modal>
      <AdminSpecialOffers
        open={offerModalOpen}
        onClose={() => setOfferModalOpen(false)}
      />
    </div>
  );
}

/** Generic modal */
function Modal({ show, onClose, title, children, maxWidth = "max-w-xl" }) {
  return (
    <Transition show={show} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child as={Fragment} enter="ease-out duration-150" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>
        <div className="fixed inset-0 grid place-items-center p-4">
          <Transition.Child as={Fragment} enter="ease-out duration-150" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-100" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
            <Dialog.Panel className={cls("w-full rounded-2xl bg-white p-5 shadow-xl", maxWidth)}>
              <div className="mb-3 flex items-center justify-between">
                <Dialog.Title className="text-base font-semibold">{title}</Dialog.Title>
                <button className="h-8 rounded-xl border px-3 text-sm" onClick={onClose}>
                  Close
                </button>
              </div>
              <div className="max-h-[70vh] overflow-auto">{children}</div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
